import { Auth, Connection, createConnection } from 'home-assistant-js-websocket';

export interface HAConnectionOptions {
  auth: Auth;
  initialBackoffMs?: number;
  maxBackoffMs?: number;
  onStateChange?: (state: HAConnectionState) => void;
}

export type HAConnectionState =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'disconnected'
  | 'reconnecting'
  | 'failed'
  | 'closed'
  | 'auth_invalid';

/**
 * Manages a long-lived HA WebSocket connection with logging + reconnect.
 *
 * The library's `Connection` already retries in-flight reconnects (~5s cap).
 * This wrapper adds exponential backoff (max 30s) for the *initial* setup,
 * surfaces state transitions to the app, and centralizes teardown.
 */
export class HAConnectionManager {
  private opts: Required<Omit<HAConnectionOptions, 'onStateChange' | 'auth'>> & {
    onStateChange?: (state: HAConnectionState) => void;
  };
  private _auth: Auth;
  private conn: Connection | null = null;
  private state: HAConnectionState = 'idle';
  private closed = false;
  private connectPromise: Promise<Connection> | null = null;

  constructor(options: HAConnectionOptions) {
    this.opts = {
      initialBackoffMs: options.initialBackoffMs ?? 1000,
      maxBackoffMs: options.maxBackoffMs ?? 30_000,
      onStateChange: options.onStateChange,
    };
    this._auth = options.auth;
  }

  get connection(): Connection | null {
    return this.conn;
  }

  get auth(): Auth {
    return this._auth;
  }

  get currentState(): HAConnectionState {
    return this.state;
  }

  async connect(): Promise<Connection> {
    if (this.closed) throw new Error('HAConnectionManager is closed');
    if (this.conn) return this.conn;
    if (this.connectPromise) return this.connectPromise;

    this.connectPromise = this.connectWithBackoff();
    try {
      const conn = await this.connectPromise;
      return conn;
    } finally {
      this.connectPromise = null;
    }
  }

  private async connectWithBackoff(): Promise<Connection> {
    let attempt = 0;
    let delay = this.opts.initialBackoffMs;

    while (!this.closed) {
      this.setState(attempt === 0 ? 'connecting' : 'reconnecting');
      try {
        const conn = await createConnection({ auth: this._auth });
        if (this.closed) { conn.close(); throw new Error("HA connection closed"); }
        this.attachEventHandlers(conn);
        this.conn = conn;
        this.setState('connected');
        return conn;
      } catch (err) {
        // ERR_INVALID_AUTH = 2 from home-assistant-js-websocket
        if (err === 2 || (err && typeof err === 'object' && 'code' in err && (err as { code: number }).code === 2)) {
          console.error('[ha] auth_invalid — session is invalid or was revoked, stopping retry');
          this.setState('auth_invalid');
          throw new Error('HA auth_invalid: session is invalid or was revoked');
        }
        attempt += 1;
        console.warn(
          `[ha] initial connect attempt ${attempt} failed, retrying in ${delay}ms`,

        );
        if (this.closed) break;
        await sleep(delay);
        delay = Math.min(delay * 2, this.opts.maxBackoffMs);
      }
    }

    this.setState('failed');
    throw new Error('HAConnectionManager closed before a connection was established');
  }

  private attachEventHandlers(conn: Connection) {
    conn.addEventListener('ready', () => {
      console.log('[ha] connection ready');
      this.setState('connected');
    });
    conn.addEventListener('disconnected', () => {
      console.warn('[ha] connection disconnected — library will attempt to reconnect');
      this.setState('disconnected');
    });
    conn.addEventListener('reconnect-error', (_c, _err) => {
      // home-assistant-js-websocket only ever fires reconnect-error for
      // ERR_INVALID_AUTH (connection.js's reconnect loop treats every other
      // failure as transient and keeps retrying) — so this is always an
      // auth failure on an already-established session (e.g. a refresh
      // token revoked while the tab was open), never a generic drop.
      // Classifying it as 'auth_invalid' (not 'failed') lets the caller's
      // onStateChange purge the now-dead stored session (fail-closed).
      console.error('[ha] reconnect error — session invalid, not retrying');
      this.setState('auth_invalid');
    });
  }

  private setState(next: HAConnectionState) {
    if (this.state === next) return;
    this.state = next;
    console.log(`[ha] state → ${next}`);
    this.opts.onStateChange?.(next);
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    if (this.conn) {
      try {
        this.conn.close();
      } catch (err) {
        console.warn('[ha] error while closing connection', err);
      }
      this.conn = null;
    }
    this.setState('closed');
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
